import { datadogRum } from '@datadog/browser-rum'
import { Component, ErrorInfo, Fragment, ReactNode } from 'react'
import { CustomErrorBoundary } from './CustomErrorBoundary'

type Props = { children: ReactNode }
type State = { error: unknown; hasError: boolean; remountKey: number }

// Browser page-translation (Google Translate et al.) reparents text nodes, leaving React's stored
// references stale; React then throws NotFoundError from removeChild/insertBefore during commit.
// These can't be told apart from genuine bugs statically, so we detect them here: tag the RUM
// report (so we can find which components to wrap) and recover by remounting the subtree instead
// of showing the full-page error. https://github.com/facebook/react/issues/11538
const isTranslateDomCrash = (error: unknown): boolean =>
  error instanceof Error && error.name === 'NotFoundError' && /removeChild|insertBefore|not a child/i.test(error.message)

// Remount budget per healthy run: a remount that immediately re-crashes (no healthy render in
// between) falls through to the error page rather than looping. A healthy render resets the budget,
// so later independent crashes can still recover.
const MAX_CONSECUTIVE_RECOVERIES = 1

export class ReactErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, remountKey: 0 }
  private consecutiveRecoveries = 0

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const translateDomCrash = isTranslateDomCrash(error)
    datadogRum.addError(error, { componentStack: info.componentStack, translateDomCrash })

    if (translateDomCrash && this.consecutiveRecoveries < MAX_CONSECUTIVE_RECOVERIES) {
      this.consecutiveRecoveries += 1
      this.setState((state) => ({ hasError: false, error: null, remountKey: state.remountKey + 1 }))
    }
  }

  componentDidUpdate(_prevProps: Props, prevState: State) {
    if (!this.state.hasError && this.state.remountKey !== prevState.remountKey) {
      this.consecutiveRecoveries = 0
    }
  }

  render() {
    if (this.state.hasError) return <CustomErrorBoundary errorData={{ error: this.state.error }} />
    return <Fragment key={this.state.remountKey}>{this.props.children}</Fragment>
  }
}
